import { Box, Fieldset, Input, Stack } from '@chakra-ui/react'
import type { FC } from 'react'
import { useEffect, useRef } from 'react'
import { useState } from 'react'
import { RRule } from 'rrule'

import { Field } from '@app/components/ui/field'
import { Switch } from '@app/components/ui/switch'
import SlackChannelAutocomplete from '@app/next/forms/autocomplete/slackChannelAutocomplete'
import InlineUpdaterRoot from '@app/next/forms/inlineUpdaterRoot'
import RRuleInput from '@app/next/forms/rruleInput'
import type { DashboardItem } from '@app/pages/aiDashboard/types'
import type { Schedule } from '@graphql/types'

interface Props {
  recurringReportConfiguration: DashboardItem
  schedule: Pick<Schedule, 'id' | 'classType' | 'rrule'>
}

const AiDashboardReportSettings: FC<Props> = ({ recurringReportConfiguration, schedule }) => {
  const [sendToSlack, setSendToSlack] = useState<boolean>(recurringReportConfiguration.sendToSlack)
  const [sendToEmail, setSendToEmail] = useState<boolean>(recurringReportConfiguration.sendToEmail)
  const sendToEmailRef = useRef<HTMLInputElement>(null)
  const sendToSlackRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (sendToEmailRef.current) {
      sendToEmailRef.current.blur()
    }
  }, [sendToEmail])

  useEffect(() => {
    if (sendToSlackRef.current) {
      sendToSlackRef.current.blur()
    }
  }, [sendToSlack])

  return (
    <Stack gap={6}>
      <Fieldset.Root borderWidth={1} borderStyle="solid" borderColor="border.default" borderRadius="md" px={6} pb={6}>
        <Fieldset.Legend>General</Fieldset.Legend>
        <Fieldset.Content>
          <Field label="Name">
            <InlineUpdaterRoot domainObject={recurringReportConfiguration}>
              <Input name="name" defaultValue={recurringReportConfiguration.name} />
            </InlineUpdaterRoot>
          </Field>
          <RRuleInput
            defaultValue={schedule?.rrule}
            name="rrule"
            availableFrequencies={[RRule.DAILY, RRule.WEEKLY, RRule.MONTHLY]}
            inline
            domainObject={schedule}
          />
        </Fieldset.Content>
      </Fieldset.Root>
      <Stack gap={6} direction={{ base: 'column', md: 'row' }}>
        <Fieldset.Root borderWidth={1} borderStyle="solid" borderColor="border.default" borderRadius="md" px={6} pb={6}>
          <Fieldset.Legend>Email</Fieldset.Legend>
          <Fieldset.Content>
            <Box css={{ '--field-label-width': '110px' }}>
              <Field label="Send to email" orientation="horizontal" alignItems="flex-end">
                <InlineUpdaterRoot domainObject={recurringReportConfiguration}>
                  <Box display="flex" justifyContent="flex-end" w="full">
                    <Switch
                      name="sendToEmail"
                      value={sendToEmail.toString()}
                      checked={sendToEmail}
                      onCheckedChange={(e) => setSendToEmail(e.checked)}
                      ref={sendToEmailRef}
                    />
                  </Box>
                </InlineUpdaterRoot>
              </Field>
            </Box>
            <Field>
              <InlineUpdaterRoot domainObject={recurringReportConfiguration}>
                <Input
                  autoComplete="email"
                  defaultValue={recurringReportConfiguration.emails || ''}
                  multiple
                  name="emails"
                  placeholder="Add an email or multiple emails seperated by a comma"
                  type="email"
                />
              </InlineUpdaterRoot>
            </Field>
          </Fieldset.Content>
        </Fieldset.Root>
        <Fieldset.Root borderWidth={1} borderStyle="solid" borderColor="border.default" borderRadius="md" px={6} pb={6}>
          <Fieldset.Legend>Slack</Fieldset.Legend>
          <Fieldset.Content>
            <Box css={{ '--field-label-width': '110px' }}>
              <Field label="Send to Slack" orientation="horizontal">
                <InlineUpdaterRoot domainObject={recurringReportConfiguration}>
                  <Box display="flex" justifyContent="flex-end" w="full">
                    <Switch
                      name="sendToSlack"
                      value={sendToSlack.toString()}
                      checked={sendToSlack}
                      onCheckedChange={(e) => setSendToSlack(e.checked)}
                      ref={sendToSlackRef}
                    />
                  </Box>
                </InlineUpdaterRoot>
              </Field>
            </Box>
            <Field>
              <InlineUpdaterRoot domainObject={recurringReportConfiguration}>
                <SlackChannelAutocomplete
                  name="slackChannelId"
                  defaultValue={recurringReportConfiguration.slackChannelId}
                  clearable
                />
              </InlineUpdaterRoot>
            </Field>
          </Fieldset.Content>
        </Fieldset.Root>
      </Stack>
    </Stack>
  )
}

AiDashboardReportSettings.displayName = 'AiDashboardReportSettings'

export default AiDashboardReportSettings
